import React, { useEffect, useMemo, useState } from "react";
import * as Mui from "@mui/material";
import dayjs from "dayjs";
import CheckIcon from "@mui/icons-material/Check";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ReceiptOutlinedIcon from "@mui/icons-material/ReceiptOutlined";
import CurrencyRupeeOutlinedIcon from "@mui/icons-material/CurrencyRupeeOutlined";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import CloseIcon from "@mui/icons-material/Close";
import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import { designColors, designRadii, designType, designWeights } from "services/constants/designTokens";
import {
  detailAccentPalette,
  detailCardSx,
  detailSectionLabelSx,
  RequestDetailField,
} from "components/DesignSystem/RequestDetailField";
import { RequestDetailHeader } from "components/DesignSystem/RequestDetailHeader";
import RequestStatusTimeline from "components/DesignSystem/RequestStatusTimeline";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { PanelActionFooter } from "components/CustomComponents/DesignSystemButtons";
import { showToast } from "services/utils/Status";
import { ToneChip } from "pages/PayRoll/Salary/Shared/salaryUi";
import { Field, FormSection, panelBodySx } from "pages/PayRoll/PayInputs/Shared/formUi";
import { DateField, SelectField, TextField } from "pages/PayRoll/PayInputs/Shared/fieldInputs";
import { CustomAutoCompleteWithLogo } from "components/CustomComponents/CustomAutoCompleteWithLogo";
import { SelectedEmployeeHeader } from "pages/TeamZone/Approve/SelectedEmployeeHeader";
import {
  claimRequestId,
  openClaimAttachment,
  submitClaim,
  updateClaim,
  useActiveReimbursementTypes,
  useReimbursementBillUpload,
  useReimbursementSettings,
} from "./reimbursementApi";

// Shared parts of the two Reimbursement claim screens:
//   My Workspace > My Requests > Reimbursement   (employee's own claims)
//   Team Workspace > Approvals > Reimbursement   (team lead approves)
// Data comes from the claim APIs (reimbursementApi.js). Bill number, Paid In
// and the payroll progress steps stay on screen and fill in once the backend
// sends bill_number / pay_month / added_to_payroll_at / paid_at.

export const PAYROLL_CUTOFF_DAY = 28;
export const API_DATE = "YYYY-MM-DD";

// Approved claims go into this month's salary when approved on or before the
// cut-off day, otherwise into next month's.
export const payMonthFor = (date) => {
  const d = dayjs(date);
  return (d.date() <= PAYROLL_CUTOFF_DAY ? d : d.add(1, "month")).startOf("month").format("YYYY-MM");
};
export const payMonthLabel = (payMonth) => (payMonth ? `${dayjs(`${payMonth}-01`).format("MMM YYYY")} salary` : "—");
export const cutoffDateFor = (payMonth) => dayjs(`${payMonth}-01`).date(PAYROLL_CUTOFF_DAY);

export const CLAIM_STATUS = {
  Pending: { label: "Pending", tone: "amber" },
  Approved: { label: "Approved", tone: "green" },
  Paid: { label: "Paid in salary", tone: "blue" },
  Rejected: { label: "Rejected", tone: "red" },
  Cancelled: { label: "Cancelled", tone: "slate" },
};

// ---------- formatting ----------

export const money = (value) =>
  value == null || value === "" ? "—" : `₹${Number(value).toLocaleString("en-IN")}`;
export const plainAmount = (value) =>
  value == null || value === "" ? "—" : Number(value).toLocaleString("en-IN");
export const formatDay = (value) => (value ? dayjs(value).format("DD MMM YYYY") : "—");
const monthName = (payMonth) => dayjs(`${payMonth}-01`).format("MMM YYYY");

// "Paid In" column: the salary the claim is (or is expected to be) paid
// with, from the claim's pay_month. Pending claims get an asterisk (see
// ClaimFootnote). "—" until the backend sends pay_month.
export const paidInText = (row) => {
  if (!row.payMonth || ["Rejected", "Cancelled"].includes(row.status)) return "—";
  return `${payMonthLabel(row.payMonth)}${row.status === "Pending" ? "*" : ""}`;
};

// Flat row for CustomFilter (search / filter / sort / download) and the table.
export const toListRow = (row) => ({
  ...row,
  request_id: row.requestId,
  employee_name: row.employee?.name || "",
  employee_code: row.employee?.code || "",
  type_name: row.typeName,
  amount: Number(row.amount),
  bill_date: row.billDate || "",
  status_label: CLAIM_STATUS[row.status]?.label || row.status,
  paid_in: paidInText(row),
  submitted_on: row.submittedAt ? dayjs(row.submittedAt).format(API_DATE) : "",
});

export const ClaimStatusChip = ({ status }) => {
  const meta = CLAIM_STATUS[status] || { label: status, tone: "slate" };
  return <ToneChip label={meta.label} tone={meta.tone} />;
};

export const PaidInCell = ({ row }) => (
  <Mui.Box component="span" sx={{ color: row.status === "Pending" ? designColors.slate500 : "inherit", whiteSpace: "nowrap" }}>
    {paidInText(row)}
  </Mui.Box>
);

export const ClaimFootnote = () => {
  const cutoff = cutoffDateFor(payMonthFor(dayjs()));
  return (
    <Mui.Typography sx={{ ...designType.captionText, px: 2, py: 1.5 }}>
      * Expected — paid with this salary if approved before the payroll cut-off ({cutoff.format("DD MMM YYYY")}). Otherwise the next salary.
    </Mui.Typography>
  );
};

// "Up to ₹2,000 a claim · bill within 60 days · bill photo required"
// Pass billUploadEnabled = false when bill upload is Off (paper bill to HR).
export const typeRuleSummary = (type, billUploadEnabled = true) => {
  if (!type) return "";
  if (type.hint) return type.hint;
  const parts = [];
  if (type.limitPerClaim != null) parts.push(`up to ${money(type.limitPerClaim)} a claim`);
  if (type.limitPerMonth != null) parts.push(`up to ${money(type.limitPerMonth)} a month`);
  if (type.limitPerClaim == null && type.limitPerMonth == null) parts.push("no limit");
  parts.push(`bill within ${type.billWithinDays} days`);
  parts.push(!type.billNeeded ? "no bill needed" : billUploadEnabled ? "bill number or photo required" : "paper bill to HR");
  const text = parts.join(" · ");
  return text.charAt(0).toUpperCase() + text.slice(1);
};

// ---------- "Before you submit" checks ----------

// Amount already claimed in a calendar month for a type by one employee
// (Pending, Approved and Paid count; Rejected and Cancelled don't).
export const claimedThisMonth = (claims, { typeId, employeeId, month, excludeId }) =>
  claims
    .filter((row) =>
      row.id !== excludeId &&
      row.typeId === typeId &&
      String(row.userId) === String(employeeId) &&
      ["Pending", "Approved", "Paid"].includes(row.status) &&
      row.billDate && dayjs(row.billDate).format("YYYY-MM") === month)
    .reduce((sum, row) => sum + Number(row.amount || 0), 0);

// Rows for the "Before you submit" box. `ok: false` rows block submitting.
// The server does not check limits or bill age yet, so these run here on
// the type's rules.
export const buildClaimChecks = ({ type, amount, billDate, claims, employeeId, approverText, excludeId }) => {
  if (!type) return [];
  const rows = [];
  const value = Number(amount) || 0;
  if (type.limitPerClaim != null) {
    rows.push({
      label: "Limit for this claim",
      value: `${money(value)} of ${money(type.limitPerClaim)}`,
      ok: value <= type.limitPerClaim,
      error: `Amount is over the ${money(type.limitPerClaim)} limit per claim.`,
    });
  }
  if (type.limitPerMonth != null) {
    const month = dayjs(billDate || undefined).format("YYYY-MM");
    const used = employeeId ? claimedThisMonth(claims, { typeId: type.id, employeeId, month, excludeId }) : 0;
    rows.push({
      label: "Limit this month",
      value: `${money(used + value)} of ${money(type.limitPerMonth)}`,
      ok: used + value <= type.limitPerMonth,
      error: `This month's claims for ${type.name} would go over ${money(type.limitPerMonth)}.`,
    });
  }
  if (type.limitPerClaim == null && type.limitPerMonth == null) {
    rows.push({ label: "Limit", value: "No limit for this type" });
  }
  if (billDate && type.billWithinDays != null) {
    const age = dayjs().startOf("day").diff(dayjs(billDate), "day");
    rows.push(
      age < 0
        ? { label: "Bill age", value: "Bill date is in the future", ok: false, error: "Bill date cannot be in the future." }
        : {
            label: "Bill age",
            value: `${age} ${age === 1 ? "day" : "days"} (allowed up to ${type.billWithinDays})`,
            ok: age <= type.billWithinDays,
            error: `The bill is older than ${type.billWithinDays} days.`,
          },
    );
  }
  if (approverText) rows.push({ label: "Approved by", value: approverText });
  const payMonth = payMonthFor(dayjs());
  rows.push({
    label: "Paid with",
    value: `${monthName(payMonth)} salary, if approved by ${cutoffDateFor(payMonth).format("DD MMM")}`,
  });
  return rows;
};

const CheckMark = ({ ok }) =>
  ok === undefined ? null : ok ? (
    <CheckIcon sx={{ fontSize: 16, color: designColors.tealDark, ml: 0.5 }} />
  ) : (
    <CloseIcon sx={{ fontSize: 16, color: "#DC2626", ml: 0.5 }} />
  );

export const ClaimChecksBox = ({ title = "Before you submit", rows, failHint = "Fix this to submit." }) => {
  if (!rows.length) return null;
  const failed = rows.some((row) => row.ok === false);
  return (
    <Mui.Box
      sx={{
        ...detailCardSx,
        borderColor: failed ? "#FECACA" : "#A7E3CF",
        backgroundColor: failed ? "#FEF2F2" : "#F0FBF6",
      }}
    >
      <Mui.Typography sx={{ ...designType.sectionHeading, mb: 1.5 }}>{title}</Mui.Typography>
      <Mui.Stack spacing={1.25}>
        {rows.map((row) => (
          <Mui.Box key={row.label} sx={{ display: "grid", gridTemplateColumns: "minmax(0, 2fr) minmax(0, 3fr)", columnGap: 2 }}>
            <Mui.Typography sx={{ ...designType.helperText }}>{row.label}</Mui.Typography>
            <Mui.Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
              <Mui.Typography sx={{ ...designType.tableBodyCell, fontWeight: designWeights.semibold, color: row.ok === false ? "#B42318" : designColors.slate900 }}>
                {row.value}
              </Mui.Typography>
              <CheckMark ok={row.ok} />
            </Mui.Box>
          </Mui.Box>
        ))}
      </Mui.Stack>
      {failed ? (
        <Mui.Typography sx={{ ...designType.captionText, color: "#B42318", mt: 1.5 }}>
          {rows.filter((row) => row.ok === false).map((row) => row.error).join(" ")} {failHint}
        </Mui.Typography>
      ) : null}
    </Mui.Box>
  );
};

// ---------- New Request panel ----------

const MAX_PHOTO_SIZE = 10 * 1024 * 1024;
const ACCEPTED_BILL_TYPES = ["image/jpeg", "image/png", "image/jpg", "image/webp", "image/heic", "application/pdf"];

const BillPhotoField = ({ value, onChange, error, disabled }) => (
  <Mui.Box>
    {value ? (
      <Mui.Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ border: `1px solid ${designColors.slate200}`, borderRadius: designRadii.card, px: 1.5, py: 1 }}
      >
        <InsertDriveFileOutlinedIcon sx={{ fontSize: 20, color: designColors.tealDark }} />
        <Mui.Typography sx={{ ...designType.tableBodyCell, flex: 1, minWidth: 0 }} noWrap>{value.name}</Mui.Typography>
        <Mui.IconButton size="small" aria-label="Remove bill photo" disabled={disabled} onClick={() => onChange(null)}>
          <CloseIcon fontSize="small" />
        </Mui.IconButton>
      </Mui.Stack>
    ) : (
      <Mui.Box
        component="label"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          py: 1.25,
          border: `1.5px dashed ${error ? "#DC2626" : designColors.slate300}`,
          borderRadius: designRadii.card,
          cursor: "pointer",
          color: designColors.tealDark,
          fontWeight: designWeights.semibold,
          "&:hover": { backgroundColor: designColors.tealTint },
        }}
      >
        <PhotoCameraOutlinedIcon sx={{ fontSize: 20 }} />
        <Mui.Typography component="span" sx={{ ...designType.tableBodyCell, color: "inherit", fontWeight: "inherit" }}>
          Take photo or upload
        </Mui.Typography>
        <input
          type="file"
          hidden
          accept="image/*,.pdf"
          capture="environment"
          disabled={disabled}
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = "";
            if (!file) return;
            if (file.type && !ACCEPTED_BILL_TYPES.includes(file.type)) {
              showToast("error", "Only photos (JPG, PNG) or PDF bills are supported.");
              return;
            }
            if (file.size > MAX_PHOTO_SIZE) {
              showToast("error", "The bill photo must be 10 MB or smaller.");
              return;
            }
            onChange({ name: file.name, file });
          }}
        />
      </Mui.Box>
    )}
  </Mui.Box>
);

const EMPTY_FORM = { employeeId: "", typeId: "", billDate: "", billNumber: "", amount: "", purpose: "", billPhoto: null };

// New Reimbursement Request. mode "self": the signed-in employee claims for
// themself (My Workspace). mode "team": a team lead raises a claim for an
// employee picked from `employees` (Team Workspace); with `approve` it is
// saved as Approved straight away (same as the old Team Apply screen).
// `claims` are the claims already loaded on the screen, for the monthly
// limit check.
export const ClaimRequestPanel = ({
  open,
  onClose,
  mode = "self",
  domain,
  companyId,
  applicant,
  employees = [],
  claims = [],
  approve = false,
  // A Pending claim to edit: the form opens filled in and saves with
  // updateClaim. The employee can't be changed while editing.
  editRow = null,
  onSubmitted,
}) => {
  const { types: activeTypes, loading: typesLoading, error: typesError } = useActiveReimbursementTypes(domain, companyId);
  const { enabled: reimbursementsEnabled, billUploadEnabled } = useReimbursementSettings(domain, companyId);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [serverErrors, setServerErrors] = useState({});
  const [showAvatar, setShowAvatar] = useState(true);
  const isTeam = mode === "team";
  const validCompany = Number(companyId) > 0;

  const isEdit = Boolean(editRow);
  useEffect(() => {
    if (open) {
      setForm(
        editRow
          ? {
              employeeId: editRow.userId ?? "",
              typeId: editRow.typeId ?? "",
              billDate: editRow.billDate || "",
              billNumber: editRow.billNumber || "",
              amount: editRow.amount ? String(editRow.amount) : "",
              purpose: editRow.purpose || "",
              billPhoto: null,
            }
          : EMPTY_FORM,
      );
      setSubmitted(false);
      setServerErrors({});
      setShowAvatar(!editRow);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editRow?.id]);

  const type = activeTypes.find((item) => item.id === form.typeId) || null;
  const listedEmployee = isTeam ? employees.find((item) => String(item.id) === String(form.employeeId)) || null : null;
  // Editing: fall back to the claim's own employee details when the
  // employee isn't in the team list.
  const employee = listedEmployee || (isTeam && editRow
    ? {
        ...editRow.employee,
        id: editRow.userId,
        raw: {
          user_name: editRow.employee?.name,
          employee_id: editRow.employee?.code,
          department_name: editRow.employee?.department,
          designation: editRow.employee?.designation,
          image_url: editRow.employee?.photo,
        },
      }
    : null);
  const existingBills = editRow?.attachments || [];
  const employeeId = isTeam ? employee?.id : applicant?.id;
  const set = (key) => (value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setServerErrors((prev) => (prev[key] ? { ...prev, [key]: "" } : prev));
  };

  const checks = buildClaimChecks({
    type,
    amount: form.amount,
    billDate: form.billDate,
    claims,
    employeeId,
    approverText: isEdit ? undefined : isTeam ? (approve ? "You (approved when added)" : "Team lead") : "Your team lead",
    excludeId: editRow?.id,
  });

  const errors = {};
  if (isTeam && !form.employeeId) errors.employeeId = "Select an employee.";
  if (!form.typeId) errors.typeId = "Select a reimbursement type.";
  else if (!type && !typesLoading) errors.typeId = "This type is no longer active. Select another type.";
  if (!form.billDate) errors.billDate = "Bill date is required.";
  if (!(Number(form.amount) > 0)) errors.amount = "Enter an amount above 0.";
  else if (Number(form.amount) >= 1e8) errors.amount = "Amount is too large.";
  if (form.purpose.trim().length < 3) errors.purpose = "Enter the purpose (at least 3 characters).";
  // With bill upload Off the paper bill goes to HR, so the bill number is
  // optional. With it On, a type that needs a bill needs a number or photo.
  const billRequired = Boolean(type?.billNeeded && billUploadEnabled);
  if (billRequired && !form.billNumber.trim() && !form.billPhoto && !existingBills.length) {
    errors.billNumber = "Add the bill number or a bill photo.";
  }
  const shown = { ...serverErrors, ...(submitted ? errors : {}) };
  const blocked = checks.some((row) => row.ok === false);

  const handleSubmit = async () => {
    setSubmitted(true);
    if (Object.keys(errors).length || blocked || saving || submitOff) return;
    setSaving(true);
    try {
      if (isEdit) {
        const updated = await updateClaim({
          domain,
          companyId,
          id: editRow.id,
          values: {
            typeId: type.id,
            billDate: form.billDate,
            billNumber: form.billNumber.trim(),
            amount: Number(form.amount),
            purpose: form.purpose.trim(),
          },
          files: form.billPhoto?.file ? [form.billPhoto.file] : [],
        });
        showToast("success", `${editRow.requestId} updated.`);
        onSubmitted?.(updated);
        onClose();
        return;
      }
      const created = await submitClaim({
        domain,
        companyId,
        approve: isTeam && approve,
        values: {
          userId: employeeId,
          typeId: type.id,
          billDate: form.billDate,
          billNumber: form.billNumber.trim(),
          amount: Number(form.amount),
          purpose: form.purpose.trim(),
        },
        files: form.billPhoto?.file ? [form.billPhoto.file] : [],
      });
      const requestId = created?.id ? claimRequestId(created.id) : "Reimbursement request";
      showToast(
        "success",
        isTeam
          ? `${requestId} added for ${employee?.name || "the employee"}${approve ? " and approved" : ""}.`
          : `${requestId} submitted. You'll be notified when it's approved.`,
      );
      onSubmitted?.(created);
      onClose();
    } catch (error) {
      const fields = error.fieldErrors || {};
      setServerErrors({
        typeId: fields.expense_type || "",
        amount: fields.amount || "",
        purpose: fields.description || "",
        employeeId: fields.user || "",
        billDate: fields.date_incurred || fields.bill_date || "",
        billNumber: fields.bill_number || "",
      });
      showToast("error", error.message || (isEdit ? "Couldn't update the claim. Please try again." : "Couldn't submit the claim. Please try again."));
    } finally {
      setSaving(false);
    }
  };

  // Enable Reimbursements off (Payroll Settings): no new claims. Editing a
  // claim that is already pending still works.
  const submitOff = !reimbursementsEnabled && !isEdit;

  const subtitle = isEdit
    ? [editRow.requestId, isTeam ? editRow.employee?.name : null].filter(Boolean).join(" · ")
    : isTeam
    ? "Raise a reimbursement claim for a team member."
    : [applicant?.name, applicant?.code, applicant?.site].filter(Boolean).join(" · ");

  const typePlaceholder = !validCompany
    ? "Select a single company at the top first"
    : typesLoading
      ? "Loading types..."
      : activeTypes.length
        ? "Select reimbursement type"
        : "No active reimbursement types";

  return (
    <GlobalSidePanel
      open={open}
      onClose={() => { if (!saving) onClose(); }}
      title={isEdit ? "Edit Reimbursement Request" : "New Reimbursement Request"}
      subtitle={subtitle}
      bodySx={panelBodySx}
      footer={
        <PanelActionFooter
          onCancel={() => { if (!saving) onClose(); }}
          primaryLabel={saving ? (isEdit ? "Saving..." : "Submitting...") : isEdit ? "Save changes" : "Submit request"}
          primaryProps={{ onClick: handleSubmit, disabled: saving || !validCompany || submitOff }}
        />
      }
    >
      <Mui.Stack spacing={2}>
        {submitOff ? (
          <Mui.Alert severity="warning">
            Reimbursements are turned off in Payroll Settings. New claims can't be submitted right now. Please contact HR.
          </Mui.Alert>
        ) : null}
        {typesError ? <Mui.Alert severity="error">Couldn't load reimbursement types: {typesError}</Mui.Alert> : null}
        {isTeam && isEdit ? (
          <FormSection title="Employee">
            <Mui.Box sx={{ gridColumn: "1 / -1", minWidth: 0 }}>
              {employee ? <SelectedEmployeeHeader selectedUser={{ ...employee.raw, label: employee.name }} /> : null}
            </Mui.Box>
          </FormSection>
        ) : isTeam ? (
          // Same employee picker (photo + name) and selected-employee card as
          // the other Team Workspace New Request panels, e.g. Comp Off.
          <FormSection title="Employee">
            <Mui.Box sx={{ gridColumn: "1 / -1", minWidth: 0 }}>
              <Field label="Employee name *" helper="Select the employee for whom the reimbursement request is being created." wide>
                <CustomAutoCompleteWithLogo
                  dataList={employees.map((item) => ({ ...item.raw, id: item.id, user_id: item.id, label: item.name, image: item.photo }))}
                  selectedValue={employee ? { ...employee.raw, id: employee.id, user_id: employee.id, label: employee.name, image: employee.photo } : null}
                  updateDataFunction={(event, data) => {
                    set("employeeId")(data ? data.user_id ?? data.id : "");
                    setShowAvatar(!data);
                  }}
                  placeHolder={employees.length ? "Select employee name" : "No employees available"}
                  errorMessage={shown.employeeId || ""}
                  textBoxError={Boolean(shown.employeeId)}
                  disableAction={saving || !employees.length}
                  showAvathar={showAvatar}
                  setShowAutoCompleteAvathar={setShowAvatar}
                />
              </Field>
              {employee ? (
                <Mui.Box sx={{ mt: 2 }}>
                  <SelectedEmployeeHeader selectedUser={{ ...employee.raw, label: employee.name }} />
                </Mui.Box>
              ) : null}
            </Mui.Box>
          </FormSection>
        ) : null}

        <FormSection title="Claim details">
          <Mui.Box sx={{ gridColumn: "1 / -1", minWidth: 0 }}>
            <Field label="Reimbursement type *" helper="Select the type of expense being claimed. Its limits and bill rules apply to this request." error={shown.typeId}>
              <SelectField
                value={form.typeId}
                onChange={(value) => set("typeId")(value === "" ? "" : Number(value))}
                options={activeTypes.map((item) => ({ value: item.id, label: item.name }))}
                placeholder={typePlaceholder}
                error={shown.typeId}
                disabled={saving || !validCompany}
              />
            </Field>
            {type ? (
              <Mui.Box sx={{ mt: 0.75 }}>
                <Mui.Typography sx={designType.captionText}>{typeRuleSummary(type, billUploadEnabled)}</Mui.Typography>
                {type.helpText ? <Mui.Typography sx={{ ...designType.captionText, mt: 0.25 }}>{type.helpText}</Mui.Typography> : null}
              </Mui.Box>
            ) : null}
          </Mui.Box>
          <Field label="Bill date *" helper="Date printed on the bill. Must be within the type's bill age limit." error={shown.billDate}>
            <DateField value={form.billDate} onChange={set("billDate")} maxDate={dayjs().format(API_DATE)} error={shown.billDate} disabled={saving} />
          </Field>
          <Field
            label={billRequired ? "Bill / ticket number" : "Bill / ticket number (optional)"}
            helper={billRequired ? "Number printed on the bill or ticket. Needed if no bill photo is added." : "Number printed on the bill or ticket, if there is one."}
            error={shown.billNumber}
          >
            <TextField value={form.billNumber} onChange={(value) => set("billNumber")(value.slice(0, 40))} placeholder="e.g. TNSTC 448812" error={shown.billNumber} disabled={saving} />
          </Field>
          <Field label="Amount (₹) *" helper="Total amount on the bill. It must stay within the type's limits." error={shown.amount}>
            <TextField
              value={form.amount}
              onChange={(value) => set("amount")(value.replace(/[^0-9.]/g, "").slice(0, 11))}
              placeholder="0"
              error={shown.amount}
              disabled={saving}
            />
          </Field>
          <Field label="Paid by" helper={isTeam ? "The employee paid and is paid back in salary." : "You paid and are paid back in your salary."}>
            <TextField value={isTeam ? "Employee (own money)" : "Myself (own money)"} onChange={() => {}} disabled />
          </Field>
          <Field label="Purpose *" helper="Briefly describe what this expense was for (at least 3 characters)." error={shown.purpose} wide>
            <TextField value={form.purpose} onChange={(value) => set("purpose")(value.slice(0, 255))} placeholder="What was this expense for?" error={shown.purpose} disabled={saving} />
          </Field>
          {type?.billNeeded && billUploadEnabled ? (
            <Mui.Box sx={{ gridColumn: "1 / -1", minWidth: 0 }}>
              <Field label="Bill photo" helper="Photo or PDF of the bill, up to 10 MB. Or enter the bill number above and give the paper bill to HR.">
                {existingBills.length ? (
                  <Mui.Typography component="div" sx={{ ...designType.captionText, mb: 1 }}>
                    Attached: <BillLinks attachments={existingBills} />
                  </Mui.Typography>
                ) : null}
                <BillPhotoField value={form.billPhoto} onChange={set("billPhoto")} error={shown.billNumber} disabled={saving} />
              </Field>
            </Mui.Box>
          ) : type?.billNeeded ? (
            <Mui.Typography sx={{ ...designType.captionText, gridColumn: "1 / -1" }}>
              Give the paper bill to HR. The bill number is optional.
            </Mui.Typography>
          ) : type ? (
            <Mui.Typography sx={{ ...designType.captionText, gridColumn: "1 / -1" }}>
              No bill needed for {type.name}.
            </Mui.Typography>
          ) : null}
        </FormSection>

        <ClaimChecksBox rows={checks} />
      </Mui.Stack>
    </GlobalSidePanel>
  );
};

// ---------- View panel ----------

const detailAccent = {
  requestId: detailAccentPalette.indigo,
  type: detailAccentPalette.blue,
  purpose: detailAccentPalette.purple,
  billDate: detailAccentPalette.indigo,
  billNumber: detailAccentPalette.pink,
  amount: detailAccentPalette.orange,
  paidIn: detailAccentPalette.green,
  bill: detailAccentPalette.blue,
  rejectReason: detailAccentPalette.pink,
  cancelReason: detailAccentPalette.purple,
};

// "2026-11-03 10:42" -> "03-11-2026, 10:42", the date format the shared
// status stepper reads.
const flowStamp = (value) => (value ? dayjs(value).format("DD-MM-YYYY, HH:mm") : undefined);

// Data for the shared RequestStatusTimeline: Requested -> Approved /
// Rejected / Cancelled -> Completed (Paid in salary shows as Completed).
export const claimStatusFlowData = (row) =>
  row
    ? {
        status: row.status === "Paid" ? "Completed" : row.status,
        applied_by: row.submittedBy || row.employee?.name || undefined,
        applied_on: flowStamp(row.submittedAt),
        approved_by: row.approvedBy || undefined,
        approved_at: flowStamp(row.approvedAt),
        rejected_by: row.rejectedBy || undefined,
        rejected_at: flowStamp(row.rejectedAt),
        rejected_reason: row.rejectReason || undefined,
        cancelled_by: row.cancelledBy || undefined,
        cancelled_at: flowStamp(row.cancelledAt),
        cancelled_reason: row.cancelReason || undefined,
        completed_at: flowStamp(row.paidAt),
      }
    : null;

// Bill files as links (inline, so they also fit inside a RequestDetailField
// value). Opening needs the login token, see openClaimAttachment.
export const BillLinks = ({ attachments }) => {
  const [opening, setOpening] = useState(null);
  if (!attachments?.length) return "Not attached";
  const open = async (file) => {
    setOpening(file.id);
    try {
      await openClaimAttachment(file.url);
    } catch (error) {
      showToast("error", error.message || "Couldn't open the bill.");
    } finally {
      setOpening(null);
    }
  };
  return (
    <Mui.Box component="span" sx={{ display: "inline-flex", flexWrap: "wrap", columnGap: 1.5, rowGap: 0.5 }}>
      {attachments.map((file) => (
        <Mui.Link
          key={file.id}
          component="button"
          type="button"
          underline="hover"
          disabled={opening === file.id}
          onClick={() => open(file)}
          sx={{ ...designType.tableBodyCell, color: designColors.tealDark, fontWeight: designWeights.semibold, textAlign: "left", overflowWrap: "anywhere" }}
        >
          {opening === file.id ? "Opening..." : file.name}
        </Mui.Link>
      ))}
    </Mui.Box>
  );
};

// Body shared by the My Workspace and Team Workspace view panels, laid out
// like the other request view panels: employee card with status, optional
// extra content (e.g. the approver checks), Request Details rows and the
// shared status stepper. Bill rows follow the type configuration.
export const ClaimRequestBody = ({ row, employee, type, billUploadEnabled = true, children }) => {
  const statusFlowData = useMemo(() => claimStatusFlowData(row), [row]);
  const hasPhoto = Boolean(row.attachments?.length);
  const noBillNeeded = type && !type.billNeeded && !row.billNumber && !hasPhoto;
  const showPhotoRow = !noBillNeeded && (billUploadEnabled || hasPhoto);
  return (
    <Mui.Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
      <RequestDetailHeader
        userDetails={{
          image_url: employee?.photo,
          employee_id: employee?.code || "-",
          user_name: employee?.name || "-",
          department_name: employee?.department || "-",
          designation: employee?.designation || "-",
        }}
        status={CLAIM_STATUS[row.status]?.label || row.status}
        colorCode={row.status === "Paid" ? "#2563EB" : undefined}
      />

      {children}

      <Mui.Box sx={detailCardSx}>
        <Mui.Typography sx={{ ...detailSectionLabelSx, mb: 1.75 }}>Request Details</Mui.Typography>
        <RequestDetailField icon={ConfirmationNumberOutlinedIcon} label="Request ID" value={row.requestId} accent={detailAccent.requestId} />
        <RequestDetailField
          icon={CategoryOutlinedIcon}
          label="Reimbursement Type"
          value={row.typeName || "-"}
          helper={typeRuleSummary(type, billUploadEnabled)}
          accent={detailAccent.type}
        />
        <RequestDetailField icon={DescriptionOutlinedIcon} label="Purpose" value={row.purpose || "-"} accent={detailAccent.purpose} multiline />
        {row.comments ? (
          <RequestDetailField icon={DescriptionOutlinedIcon} label="Comments" value={row.comments} accent={detailAccent.purpose} multiline />
        ) : null}
        <RequestDetailField icon={CalendarMonthIcon} label="Bill Date" value={formatDay(row.billDate)} accent={detailAccent.billDate} />
        <RequestDetailField
          icon={ReceiptOutlinedIcon}
          label="Bill Number"
          value={noBillNeeded ? `No bill needed for ${type.name}` : row.billNumber || "-"}
          accent={detailAccent.billNumber}
        />
        <RequestDetailField icon={CurrencyRupeeOutlinedIcon} label="Amount Claimed" value={money(row.amount)} accent={detailAccent.amount} />
        {row.amountApproved != null ? (
          <RequestDetailField icon={CurrencyRupeeOutlinedIcon} label="Amount Approved" value={money(row.amountApproved)} accent={detailAccent.amount} />
        ) : null}
        <RequestDetailField
          icon={AccountBalanceOutlinedIcon}
          label="Paid In"
          value={paidInText(row)}
          accent={detailAccent.paidIn}
          isLast={!showPhotoRow && !row.rejectReason && !row.cancelReason}
        />
        {showPhotoRow ? (
          <RequestDetailField
            icon={AttachFileOutlinedIcon}
            label="Bill Photo"
            value={<BillLinks attachments={row.attachments} />}
            accent={detailAccent.bill}
            isLast={!row.rejectReason && !row.cancelReason}
          />
        ) : null}
        {row.rejectReason ? (
          <RequestDetailField
            icon={DescriptionOutlinedIcon}
            label="Reject Reason"
            value={row.rejectReason}
            accent={detailAccent.rejectReason}
            multiline
            isLast={!row.cancelReason}
          />
        ) : null}
        {row.cancelReason ? (
          <RequestDetailField icon={DescriptionOutlinedIcon} label="Cancel Reason" value={row.cancelReason} accent={detailAccent.cancelReason} multiline isLast />
        ) : null}
      </Mui.Box>

      <RequestStatusTimeline data={statusFlowData} variant="stepper" hidePendingStep />
    </Mui.Box>
  );
};

// My Workspace view panel, same layout as the other My Workspace request
// view panels (Overtime, Leave...): "Reimbursement Request" header, the
// shared body above, actions in the footer.
export const ClaimViewPanel = ({ open, row, onClose, employee, types = [], domain, companyId, headerActions, footer, expanded, onToggleExpand, breadcrumb }) => {
  const billUploadEnabled = useReimbursementBillUpload(domain, companyId);
  if (!row) return null;
  const type = types.find((item) => item.id === row.typeId) || null;
  return (
    <GlobalSidePanel
      open={open}
      onClose={onClose}
      expanded={expanded}
      onToggleExpand={onToggleExpand}
      breadcrumb={breadcrumb}
      title="Reimbursement Request"
      subtitle="Request details and status"
      headerActions={headerActions}
      headerSx={{ px: 2.5, pt: 2.5, pb: 1.5 }}
      footer={footer}
    >
      <ClaimRequestBody row={row} employee={employee || row.employee} type={type} billUploadEnabled={billUploadEnabled} />
    </GlobalSidePanel>
  );
};
